import React, { useState, useEffect, useRef } from 'react';

// API servisleri
const API_BASE_URL = 'https://europe-west3-bruh-bbadb.cloudfunctions.net';

// Firebase Storage için yapılandırma
const FIREBASE_STORAGE_BUCKET = 'bruh-bbadb.firebasestorage.app';
const STORAGE_BASE_PATH = 'bruhmanadventures';

// Token'ı localStorage'dan alacak yardımcı fonksiyon
const getAuthToken = () => {
  return localStorage.getItem('userToken');
};

// Card bilgisini localStorage'dan alacak yardımcı fonksiyon
const getAuthCard = () => {
  return localStorage.getItem('card');
};

// Hata durumunda login sayfasına yönlendirme
const redirectToLogin = () => {
  localStorage.removeItem('userToken');
  localStorage.removeItem('card');
  window.location.href = '/mlogin';
};

// API isteklerini sarmalayan yardımcı fonksiyon
const makeApiRequest = async (url, options = {}) => {
  const token = getAuthToken();
  const card = getAuthCard();

  console.log('🔐 Token:', token ? 'Exists' : 'Missing');
  console.log('🎫 Card:', card ? card : 'Missing');

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Token varsa Authorization header'ını ekle
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let requestBody = options.body;

  // Body varsa ve card eklenmemişse card'ı ekle
  if (requestBody && card) {
    try {
      const bodyData = JSON.parse(requestBody);
      if (!bodyData.card) {
        bodyData.card = card;
        requestBody = JSON.stringify(bodyData);
        console.log('✅ Added card to request body:', card);
      }
    } catch (error) {
      console.log('❌ Could not parse request body');
    }
  }

  const config = {
    ...options,
    headers,
    body: requestBody,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Backend'den gelen redirectToLogin flag'ini kontrol et
      if (errorData.redirectToLogin) {
        redirectToLogin();
        throw new Error('Authentication failed. Redirecting to login.');
      }

      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

// Blob'u base64'e çevir
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

const apiService = {
  // Kategorileri getir
  async getCategories() {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/getcategories`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return response;
    } catch (error) {
      console.error('Kategori çekme hatası:', error);
      throw error;
    }
  },

  // Ürünleri getir
  async getProductsByCategory(card) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/getproductsbycategory`, {
        method: 'POST',
        body: JSON.stringify({ card }),
      });
      return response;
    } catch (error) {
      console.error('Ürün çekme hatası:', error);
      throw error;
    }
  },

  // Tüm kategorilerin ürünlerini getir
  async getAllProducts(categories) {
    try {
      console.log('🔄 Tüm kategorilerin ürünleri yükleniyor...');

      const productPromises = categories.map(category =>
        this.getProductsByCategory(category.card)
      );

      const productsResponses = await Promise.all(productPromises);

      const allProducts = {};
      categories.forEach((category, index) => {
        if (productsResponses[index].success) {
          allProducts[category.liability] = productsResponses[index].products;
        } else {
          console.error(`❌ Ürünler yüklenemedi for category: ${category.label}`);
          allProducts[category.liability] = [];
        }
      });

      console.log('✅ Tüm ürünler yüklendi:', Object.keys(allProducts).length, 'kategori');
      return allProducts;
    } catch (error) {
      console.error('Tüm ürünleri yükleme hatası:', error);
      throw error;
    }
  },

  // R2'ye resim yükle (TEK YÖNTEM)
  async uploadImageToR2(file, fileName, folder = 'uploads') {
    console.log('🚀 R2\'ye resim yükleniyor:', fileName);

    // Authentication kontrolü
    const token = getAuthToken();
    const card = getAuthCard();

    if (!token || !card) {
      throw new Error('Authentication failed. Please login again.');
    }

    // File -> base64 dönüşümü
    const base64 = await blobToBase64(file);
    
    // Content type kontrolü
    const contentType = file.type || 'image/jpeg';

    const response = await makeApiRequest(`${API_BASE_URL}/uploadimagetor2`, {
      method: 'POST',
      body: JSON.stringify({
        card,
        base64,
        contentType,
        folder,
        filename: fileName
      })
    });

    if (response.success && response.result && response.result.url) {
      console.log('✅ R2 upload başarılı:', response.result.url);
      return response.result.url;
    } else {
      throw new Error(response.message || 'R2 upload failed: ' + (response.error || 'Unknown error'));
    }
  },

  // Yeni kategori ekle
  async addCategory(categoryData) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/addcategory`, {
        method: 'POST',
        body: JSON.stringify(categoryData),
      });
      return response;
    } catch (error) {
      console.error('Kategori ekleme hatası:', error);
      throw error;
    }
  },

  // Kategori güncelle
  async updateCategory(categoryData) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/updatecategory`, {
        method: 'POST',
        body: JSON.stringify(categoryData),
      });
      return response;
    } catch (error) {
      console.error('Kategori güncelleme hatası:', error);
      throw error;
    }
  },

  // Kategori sil
  async deleteCategory(liability) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/deletecategory`, {
        method: 'POST',
        body: JSON.stringify({ liability }),
      });
      return response;
    } catch (error) {
      console.error('Kategori silme hatası:', error);
      throw error;
    }
  },

  // Tüm ürünleri kaydet
  async saveProducts(products) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/saveproducts`, {
        method: 'POST',
        body: JSON.stringify({ products }),
      });
      return response;
    } catch (error) {
      console.error('Ürün kaydetme hatası:', error);
      throw error;
    }
  },

  // Tek ürün kaydet
  async saveProduct(product) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/saveproduct`, {
        method: 'POST',
        body: JSON.stringify({ product }),
      });
      return response;
    } catch (error) {
      console.error('Ürün kaydetme hatası:', error);
      throw error;
    }
  },

  // Yeni ürün ekle (iteminfo'ya)
  async addProduct(productData) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/addproduct`, {
        method: 'POST',
        body: JSON.stringify(productData),
      });
      return response;
    } catch (error) {
      console.error('Ürün ekleme hatası:', error);
      throw error;
    }
  },

  // Ürün sil
  async removeProduct(uniqcode) {
    try {
      const response = await makeApiRequest(`${API_BASE_URL}/removeproduct`, {
        method: 'POST',
        body: JSON.stringify({ uniqcode }),
      });
      return response;
    } catch (error) {
      console.error('Ürün silme hatası:', error);
      throw error;
    }
  },
};

// Resim ön yükleme fonksiyonu
const preloadImage = (src) => {
  return new Promise((resolve) => {
    if (!src) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => {
      console.log('✅ Resim ön yüklendi:', src);
      resolve(true);
    };
    img.onerror = () => {
      console.log('❌ Resim ön yükleme hatası:', src);
      resolve(false);
    };
    img.src = src;
  });
};

// Tüm resimleri ön yükleme fonksiyonu
const preloadAllImages = async (allProductsData, categories) => {
  console.log('🔄 Tüm resimler ön yükleniyor...');

  const imageUrls = new Set();

  // Kategori resimlerini ekle
  categories.forEach(category => {
    if (category.icon) imageUrls.add(category.icon);
  });

  // Tüm ürünlerdeki resim URL'lerini topla
  Object.values(allProductsData).forEach(products => {
    products.forEach(product => {
      if (product.image) imageUrls.add(product.image);

      // Question resimleri
      if (product.questions) {
        product.questions.forEach(question => {
          if (question.image) imageUrls.add(question.image);

          // Option resimleri
          if (question.options) {
            question.options.forEach(option => {
              if (option.image) imageUrls.add(option.image);
            });
          }
        });
      }
    });
  });

  console.log(`📸 ${imageUrls.size} resim ön yüklenecek`);

  // Tüm resimleri paralel olarak ön yükle
  const preloadPromises = Array.from(imageUrls).map(url => preloadImage(url));
  const results = await Promise.allSettled(preloadPromises);

  const successful = results.filter(result => result.status === 'fulfilled' && result.value).length;
  console.log(`✅ ${successful}/${imageUrls.size} resim başarıyla ön yüklendi`);
};

// ImageWithFallback Component
const ImageWithFallback = ({ src, alt, className, type = "product", showChangeButton = false, onChange, categoryId, questionIndex, optionIndex }) => {
  const [imageError, setImageError] = useState(!src);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (src) {
      setImageError(false);
      setImageLoaded(false);

      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  const dimensions = type === "product" ? "w-48 h-48" : type === "category" ? "w-12 h-12" : "w-32 h-32";

  return (
    <div className={`relative ${dimensions} bg-gray-200 rounded-lg flex items-center justify-center ${className}`}>
      {!imageError && src ? (
        <>
          <div className={`${dimensions} relative`}>
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg flex items-center justify-center">
                <div className="text-gray-500 text-xs">Yükleniyor...</div>
              </div>
            )}
            <img
              src={src}
              alt={alt}
              className={`${dimensions} object-cover rounded-lg transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
            {showChangeButton && (
              <label className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex flex-col items-center justify-center cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
                <span className="text-white text-2xl mb-1">📷</span>
                <span className="text-white text-sm font-medium">Change Photo</span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => {
                    if (onChange) {
                      onChange(e, questionIndex, optionIndex, categoryId);
                    }
                  }}
                />
              </label>
            )}
          </div>
        </>
      ) : (
        <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center p-1">
          <span className="text-xs">No Photo</span>
          {showChangeButton && (
            <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer">
              <span className="text-white text-2xl mb-1">📷</span>
              <span className="text-white text-sm font-medium">Add Photo</span>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => {
                  if (onChange) {
                    onChange(e, questionIndex, optionIndex, categoryId);
                  }
                }}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
};

// Product Image Component
const ProductImage = ({ src, alt, onImageChange, uploading = false }) => {
  const [imageError, setImageError] = useState(!src);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (src) {
      setImageError(false);
      setImageLoaded(false);

      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="relative w-48 h-48 bg-gray-200 rounded-lg flex items-center justify-center">
      {!imageError && src ? (
        <>
          <div className="w-48 h-48 relative">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg flex items-center justify-center">
                <div className="text-gray-500 text-xs">Yükleniyor...</div>
              </div>
            )}
            <img
              src={src}
              alt={alt}
              className={`w-48 h-48 object-cover rounded-lg transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </div>
          <label className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex flex-col items-center justify-center cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
            <span className="text-white text-2xl mb-1">📷</span>
            <span className="text-white text-sm font-medium">Change Photo</span>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={onImageChange}
            />
          </label>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
          <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center p-1">
            <span className="text-white text-2xl mb-1">📷</span>
            <span className="text-white text-sm font-medium">Add Photo</span>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={onImageChange}
            />
          </div>
        </label>
      )}
      {uploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
            <p className="text-sm">Yükleniyor...</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Option Image Component
const OptionImage = ({ src, alt, onImageChange, uploading = false }) => {
  const [imageError, setImageError] = useState(!src);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (src) {
      setImageError(false);
      setImageLoaded(false);

      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="relative w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
      {!imageError && src ? (
        <>
          <div className="w-32 h-32 relative">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg flex items-center justify-center">
                <div className="text-gray-500 text-xs">Yükleniyor...</div>
              </div>
            )}
            <img
              src={src}
              alt={alt}
              className={`w-32 h-32 object-cover rounded-lg transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          </div>
          <label className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex flex-col items-center justify-center cursor-pointer opacity-0 hover:opacity-100 transition-opacity">
            <span className="text-white text-lg mb-1">📷</span>
            <span className="text-white text-xs font-medium">Change</span>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={onImageChange}
            />
          </label>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
          <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center p-1">
            <span className="text-white text-lg mb-1">📷</span>
            <span className="text-white text-xs font-medium">Add Photo</span>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={onImageChange}
            />
          </div>
        </label>
      )}
      {uploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-1"></div>
            <p className="text-xs">Yükleniyor...</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ProductPreviewCard Component
const ProductPreviewCard = ({ product }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(!product?.image);

  useEffect(() => {
    if (product?.image) {
      setImageError(false);
      setImageLoaded(false);

      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = product.image;
    }
  }, [product?.image]);

  if (!product) return null;

  return (
    <div className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 cursor-default w-[216px] h-[290.75px] mx-auto">
      <div className="relative h-48 overflow-hidden">
        {product.image && !imageError ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg flex items-center justify-center">
                <div className="text-gray-500 text-xs">Yükleniyor...</div>
              </div>
            )}
            <img
              alt={product.name}
              className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              src={product.image}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </>
        ) : (
          <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center">
            <div className="text-gray-500 text-sm font-medium">No Photo</div>
          </div>
        )}

        <div className="absolute top-3 right-3 bg-white text-gray-900 px-4 py-2 rounded-full shadow-lg font-bold text-base">
          ${product.basePrice || 0}
        </div>

        <div className="absolute bottom-3 left-3 bg-blue-500 text-white px-3 py-1 rounded-full font-bold text-xs">
          Preview
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors line-clamp-1">
          {product.name}
        </h3>
        <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">
          {product.description || "No description available"}
        </p>
      </div>
    </div>
  );
};

// Kategori listesindeki resimler için basit Image component'i
const CategoryImage = ({ src, alt }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(!src);

  useEffect(() => {
    if (src) {
      setImageError(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
      {!imageError && src ? (
        <div className="w-12 h-12 relative">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg"></div>
          )}
          <img
            src={src}
            alt={alt}
            className={`w-12 h-12 object-cover rounded-lg transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="text-gray-500 text-xs">No Photo</div>
      )}
    </div>
  );
};

// Product listesindeki resimler için basit component
const ProductListImage = ({ src, alt }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(!src);

  useEffect(() => {
    if (src) {
      setImageError(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [src]);

  return (
    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
      {!imageError && src ? (
        <div className="w-16 h-16 relative">
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-300 animate-pulse rounded-lg"></div>
          )}
          <img
            src={src}
            alt={alt}
            className={`w-16 h-16 object-cover rounded-lg transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </div>
      ) : (
        <div className="text-gray-400 text-xs text-center">
          <span>No Photo</span>
        </div>
      )}
    </div>
  );
};

const MerchantProductManagement = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(0);
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryData, setEditingCategoryData] = useState({
    label: '',
    icon: '',
    oldLabel: ''
  });
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    type: 'multiple',
    isRequired: false
  });
  const [newCategory, setNewCategory] = useState({
    label: '',
    icon: '',
    iconFile: null
  });
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [priceErrors, setPriceErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasCategoryUnsavedChanges, setHasCategoryUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingProductIndex, setPendingProductIndex] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [allProductsData, setAllProductsData] = useState({});
  const [imagesPreloaded, setImagesPreloaded] = useState(false);

  const questionRefs = useRef([]);
  const questionsSectionRef = useRef(null);

  // ✅ Kategoriye göre güvenli ürün listesi alma
  const getCategoryProducts = (category) => {
    if (!category) return [];
    const arr = allProductsData[category.liability] || [];
    // Backend fazla ürün döndürse bile liability ile kırp
    return arr.filter(p => p?.liability === category.liability);
  };

  // ✅ Doğru ürün sayısı
  const getCategoryProductCount = (category) => {
    return getCategoryProducts(category).length;
  };

  // Sayfa yüklendiğinde token ve card kontrolü
  useEffect(() => {
    console.log('🔍 Page loaded, checking token and card...');
    const token = getAuthToken();
    const card = getAuthCard();

    console.log('🔐 Token:', token ? 'Exists' : 'Missing');
    console.log('🎫 Card:', card ? card : 'Missing');

    if (!token || !card) {
      console.log('❌ Token or card missing, redirecting to login');
      redirectToLogin();
      return;
    }

    console.log('✅ Token and card found, loading initial data');
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      setInitialLoading(true);
      setError(null);

      console.log('🔄 Kategoriler yükleniyor...');
      const categoriesResponse = await apiService.getCategories();

      if (categoriesResponse.success) {
        const categoriesData = categoriesResponse.categories;
        console.log('✅ Kategoriler yüklendi:', categoriesData.length);
        setCategories(categoriesData);
        setOriginalCategories(JSON.parse(JSON.stringify(categoriesData)));

        if (categoriesData.length > 0) {
          console.log('🔄 Tüm kategorilerin ürünleri yükleniyor...');
          const allProducts = await apiService.getAllProducts(categoriesData);
          setAllProductsData(allProducts);

          console.log('🔄 Resimler ön yükleniyor...');
          await preloadAllImages(allProducts, categoriesData);
          setImagesPreloaded(true);

          const firstCategory = categoriesData[0];
          setSelectedCategory(firstCategory);

          // ✅ yalnızca seçilen kategori ürünlerini yükle
          const firstCatProducts = (allProducts[firstCategory.liability] || []).filter(
            p => p?.liability === firstCategory.liability
          );

          setProducts(firstCatProducts);
          setOriginalProducts(JSON.parse(JSON.stringify(firstCatProducts)));

          console.log('✅ Tüm ürünler yüklendi, ilk kategori gösteriliyor:', firstCategory.label);
        }

        setInitialLoading(false);
      } else {
        throw new Error(categoriesResponse.message || 'Kategoriler çekilemedi');
      }
    } catch (err) {
      console.error('Veri yükleme hatası:', err);
      setError(err.message);
      setInitialLoading(false);
    }
  };

  // ✅ ÖNEMLİ FIX:
  // Seçili kategori değişince products yükle.
  // allProductsData değişince selectedProduct sıfırlama hatasını kaldırıyoruz.
  useEffect(() => {
    if (selectedCategory) {
      const catProducts = getCategoryProducts(selectedCategory);

      console.log('🔄 Seçili kategori değişti, ürünler gösteriliyor:', selectedCategory.label);
      setProducts(catProducts);
      setOriginalProducts(JSON.parse(JSON.stringify(catProducts)));
      setSelectedProduct(0);
      setActiveQuestion(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Filtrelenmiş ürünleri doğru şekilde hesapla
  const filteredProducts = products.filter(product =>
    product.liability === selectedCategory?.liability
  );

  // Mevcut ürünü doğru şekilde bul
  const currentProduct = filteredProducts[selectedProduct] || filteredProducts[0];
  const currentCategory = selectedCategory;

  // Değişiklikleri kontrol et
  useEffect(() => {
    const productsChanged = JSON.stringify(products) !== JSON.stringify(originalProducts);
    setHasUnsavedChanges(productsChanged);
  }, [products, originalProducts]);

  useEffect(() => {
    const categoriesChanged = JSON.stringify(categories) !== JSON.stringify(originalCategories);
    setHasCategoryUnsavedChanges(categoriesChanged);
  }, [categories, originalCategories]);

  // Kategori Düzenleme Fonksiyonları
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setEditingCategoryData({
      label: category.label,
      icon: category.icon,
      oldLabel: category.label
    });
    setShowEditCategoryModal(true);
  };

  const handleEditCategorySave = async () => {
    if (editingCategory && editingCategoryData.label.trim()) {
      // 1. Kategori adını temizle
      const newCategoryName = editingCategoryData.label.trim();
      
      // 2. Eğer kategori adı değişmediyse kontrol etme (kendisi hariç)
      if (newCategoryName.toLowerCase() !== editingCategory.label.toLowerCase()) {
        // 3. Aynı isimde başka kategori var mı kontrol et
        const existingCategory = categories.find(
          cat => 
            cat.id !== editingCategory.id && // Kendisi hariç
            cat.label.toLowerCase() === newCategoryName.toLowerCase()
        );

        if (existingCategory) {
          setError(`"${newCategoryName}" isimli bir kategori zaten mevcut! Aynı isimde 2. kategori oluşturulamaz.`);
          return;
        }
      }

      try {
        setSaving(true);
        setError(null);

        const newId = newCategoryName;

        const updateData = {
          liability: editingCategory.liability,
          id: newId,
          label: newCategoryName,
          icon: editingCategoryData.icon,
          oldLabel: editingCategoryData.oldLabel
        };

        const response = await apiService.updateCategory(updateData);

        if (response.success) {
          const updatedCategories = categories.map(cat =>
            cat.liability === editingCategory.liability
              ? {
                ...cat,
                id: newId,
                label: newCategoryName,
                icon: editingCategoryData.icon
              }
              : cat
          );

          setCategories(updatedCategories);
          setOriginalCategories(JSON.parse(JSON.stringify(updatedCategories)));

          // AllProductsData'yı güncelle
          const updatedAllProducts = { ...allProductsData };
          if (updatedAllProducts[editingCategory.liability]) {
            const updatedProducts = updatedAllProducts[editingCategory.liability].map(product => ({
              ...product,
              category: newCategoryName
            }));
            updatedAllProducts[editingCategory.liability] = updatedProducts;
            setAllProductsData(updatedAllProducts);

            // Eğer seçili kategori bu ise products state'ini de güncelle
            if (selectedCategory?.liability === editingCategory.liability) {
              const safe = updatedProducts.filter(p => p?.liability === editingCategory.liability);
              setProducts(safe);
              setOriginalProducts(JSON.parse(JSON.stringify(safe)));
            }
          }

          if (selectedCategory?.liability === editingCategory.liability) {
            setSelectedCategory(updatedCategories.find(cat => cat.liability === editingCategory.liability));
          }

          setShowEditCategoryModal(false);
          setEditingCategory(null);
          setEditingCategoryData({ label: '', icon: '', oldLabel: '' });

          setShowSaveSuccess(true);
          setTimeout(() => {
            setShowSaveSuccess(false);
          }, 3000);
        } else {
          throw new Error(response.message || 'Kategori güncellenirken hata oluştu');
        }
      } catch (error) {
        console.error('Kategori güncelleme hatası:', error);
        setError('Kategori güncellenirken hata oluştu: ' + error.message);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleEditCategoryCancel = () => {
    setShowEditCategoryModal(false);
    setEditingCategory(null);
    setEditingCategoryData({ label: '', icon: '', oldLabel: '' });
  };

  // Ürünleri kaydetme yardımcı fonksiyonu
  const saveCurrentProducts = async () => {
    if (!currentProduct || !currentCategory) return false;

    try {
      setSaving(true);
      setError(null);

      const productsToSave = products.filter(product =>
        product.liability === currentCategory.liability
      );

      console.log('💾 Kaydedilecek ürünler:', productsToSave);

      const updatedProducts = productsToSave.map(product => ({
        ...product,
        card: currentCategory.card,
        liability: currentCategory.liability,
        category: currentCategory.label,
        hasQuestions: product.questions && Array.isArray(product.questions) && product.questions.length > 0
      }));

      const response = await apiService.saveProducts(updatedProducts);

      if (response.success) {
        const updatedOriginalProducts = JSON.parse(JSON.stringify(products));
        setOriginalProducts(updatedOriginalProducts);

        setAllProductsData(prev => ({
          ...prev,
          [currentCategory.liability]: updatedOriginalProducts
        }));

        setHasUnsavedChanges(false);

        setShowSaveSuccess(true);
        setTimeout(() => {
          setShowSaveSuccess(false);
        }, 3000);

        console.log('✅ Ürünler başarıyla kaydedildi:', response.summary);
        return true;
      } else {
        throw new Error(response.message || 'Ürünler kaydedilirken hata oluştu');
      }
    } catch (error) {
      console.error('Ürün kaydetme hatası:', error);
      setError('Ürünler kaydedilirken hata oluştu: ' + error.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Ana Save Changes fonksiyonu
  const handleSaveChanges = async () => {
    await saveCurrentProducts();
  };

  // Tek ürün kaydetme fonksiyonu
  const handleSaveSingleProduct = async (product) => {
    if (!product || !currentCategory) return false;

    try {
      setSaving(true);
      setError(null);

      const hasQuestions =
        product.questions &&
        Array.isArray(product.questions) &&
        product.questions.length > 0;

      const productToSave = {
        ...product,
        card: currentCategory.card,
        liability: currentCategory.liability,
        category: currentCategory.label,
        hasQuestions
      };

      console.log('💾 Kaydedilecek ürün:', productToSave);

      const response = await apiService.saveProduct(productToSave);

      if (response.success) {
        console.log('✅ Ürün başarıyla kaydedildi:', response.product);
        return true;
      } else {
        throw new Error(response.message || 'Ürün kaydedilirken hata oluştu');
      }
    } catch (error) {
      console.error('Ürün kaydetme hatası:', error);
      setError('Ürün kaydedilirken hata oluştu: ' + error.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Ürün field'larını güncelleme fonksiyonları
  const updateProductField = (field, value) => {
    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);
    if (productIndex !== -1) {
      updatedProducts[productIndex][field] = value;
      setProducts(updatedProducts);
    }
    setEditingField(null);
  };

  const updateQuestionField = (questionIndex, field, value) => {
    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);
    if (productIndex !== -1) {
      updatedProducts[productIndex].questions[questionIndex][field] = value;
      setProducts(updatedProducts);
    }
    setEditingField(null);
  };

  const updateOptionField = (questionIndex, optionIndex, field, value) => {
    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);
    if (productIndex !== -1) {
      updatedProducts[productIndex].questions[questionIndex].options[optionIndex][field] = value;
      setProducts(updatedProducts);
    }
    setEditingField(null);
  };

  // Resim yükleme fonksiyonu - GÜNCELLENMİŞ (R2 ÖNCELİKLİ) - FIXED
  const handleImageUpload = async (event, questionIndex, optionIndex, categoryId) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      const maxSize = 8 * 1024 * 1024; // 8MB (R2 limiti)
      if (file.size > maxSize) {
        setError('Dosya boyutu 8MB\'dan küçük olmalıdır');
        setUploading(false);
        return;
      }

      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `image_${timestamp}.${fileExtension}`;

      console.log('📤 Yüklenen dosya:', {
        name: file.name,
        type: file.type,
        size: file.size,
        extension: fileExtension
      });

      // Klasör belirle
      let folder = 'uploads';
      if (categoryId !== undefined) {
        folder = 'categories';
      } else if (questionIndex !== undefined && optionIndex !== undefined) {
        folder = 'options';
      } else if (questionIndex !== undefined) {
        folder = 'questions';
      } else {
        folder = 'products';
      }

      // R2'ye yükle - ana yöntem
      const imageUrl = await apiService.uploadImageToR2(file, fileName, folder);

      // State güncelle
      let productUpdated = false;
      let updatedProduct = null;

      if (categoryId !== undefined) {
        // Kategori icon güncelle
        const updatedCategories = [...categories];
        const categoryIndex = updatedCategories.findIndex(cat => cat.id === categoryId);
        if (categoryIndex !== -1) {
          const category = updatedCategories[categoryIndex];
          updatedCategories[categoryIndex].icon = imageUrl;
          setCategories(updatedCategories);

          // Backend'e kaydet
          try {
            const updateData = {
              liability: category.liability,
              id: category.id,
              label: category.label,
              icon: imageUrl,
              oldLabel: category.label
            };
            await apiService.updateCategory(updateData);
            
            // Original state'i güncelle
            const updatedOriginalCategories = [...originalCategories];
            const originalCategoryIndex = updatedOriginalCategories.findIndex(cat => cat.id === categoryId);
            if (originalCategoryIndex !== -1) {
              updatedOriginalCategories[originalCategoryIndex].icon = imageUrl;
              setOriginalCategories(updatedOriginalCategories);
            }
            console.log('✅ Kategori icon R2\'ye kaydedildi');
          } catch (error) {
            console.error('❌ Kategori icon kaydetme hatası:', error);
            // Rollback
            updatedCategories[categoryIndex].icon = category.icon;
            setCategories(updatedCategories);
            setError('Icon güncellenirken hata oluştu: ' + error.message);
          }
        }
      } else if (questionIndex !== undefined && optionIndex !== undefined) {
        // Option image güncelle
        const updatedProducts = [...products];
        const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);
        if (productIndex !== -1) {
          updatedProducts[productIndex].questions[questionIndex].options[optionIndex].image = imageUrl;
          setProducts(updatedProducts);
          productUpdated = true;
          updatedProduct = updatedProducts[productIndex];
        }
      } else if (questionIndex !== undefined) {
        // Question image güncelle
        const updatedProducts = [...products];
        const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);
        if (productIndex !== -1) {
          updatedProducts[productIndex].questions[questionIndex].image = imageUrl;
          setProducts(updatedProducts);
          productUpdated = true;
          updatedProduct = updatedProducts[productIndex];
        }
      } else {
        // Product image güncelle
        const updatedProducts = [...products];
        const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);
        if (productIndex !== -1) {
          updatedProducts[productIndex].image = imageUrl;
          setProducts(updatedProducts);
          productUpdated = true;
          updatedProduct = updatedProducts[productIndex];
        }
      }

      // Eğer ürün güncellendiyse kaydet
      if (productUpdated && updatedProduct) {
        const success = await handleSaveSingleProduct(updatedProduct);
        if (success) {
          console.log('✅ Resim değişikliği kaydedildi');
        }
      }

      // Yükleme tamamlandı - uploading'i false yap
      setUploading(false);

    } catch (error) {
      console.error('❌ Resim yükleme hatası:', error);
      setError('Resim yüklenirken hata oluştu: ' + error.message);
      setUploading(false);
      return;
    }
  };

  // Product image upload handler
  const handleProductImageUpload = async (event) => {
    await handleImageUpload(event);
  };

  // Option image upload handler
  const handleOptionImageUpload = async (event, questionIndex, optionIndex) => {
    await handleImageUpload(event, questionIndex, optionIndex);
  };

  // Yeni ürün ekleme
  const addNewProduct = () => {
    if (hasUnsavedChanges) {
      setPendingAction('new');
      setShowUnsavedChangesModal(true);
      return;
    }

    executeAddNewProduct();
  };

  const executeAddNewProduct = async () => {
    try {
      const newUniqcode = `uniq_${Date.now()}`;
      const card = getAuthCard();

      const newProductData = {
        card: card,
        category: currentCategory.label,
        name: "New Product",
        price: "£0",
        desc: "Product description",
        image: "",
        uniqcode: newUniqcode,
        hasQuestions: false
      };

      console.log("📤 iteminfo'ya yeni ürün ekleniyor:", newProductData);

      const addResponse = await apiService.addProduct(newProductData);

      if (addResponse.success) {
        console.log("✅ Ürün iteminfo'ya başarıyla eklendi");

        const newProduct = {
          id: Date.now().toString(),
          basePrice: 0,
          image: "",
          name: "New Product",
          description: "Product description",
          questions: [],
          active: true,
          liability: currentCategory.liability,
          category: currentCategory.label,
          uniqcode: newUniqcode,
          card: card,
          hasQuestions: false
        };

        const updatedProducts = [...products, newProduct];
        setProducts(updatedProducts);

        setAllProductsData(prev => ({
          ...prev,
          [currentCategory.liability]: updatedProducts
        }));

        setSelectedProduct(updatedProducts.length - 1);

        const success = await handleSaveSingleProduct(newProduct);
        if (success) {
          setOriginalProducts(JSON.parse(JSON.stringify(updatedProducts)));
          setHasUnsavedChanges(false);

          setShowSaveSuccess(true);
          setTimeout(() => {
            setShowSaveSuccess(false);
          }, 3000);

          console.log("✅ Ürün designday'a da başarıyla kaydedildi");
        }
      } else {
        throw new Error(addResponse.message || 'Ürün iteminfo\'ya eklenirken hata oluştu');
      }

    } catch (error) {
      console.error('❌ Yeni ürün ekleme hatası:', error);
      setError('Yeni ürün eklenirken hata oluştu: ' + error.message);
    }
  };

  // Ürün silme
  const removeProduct = async () => {
    if (currentProduct) {
      try {
        setSaving(true);

        const response = await apiService.removeProduct(currentProduct.uniqcode);

        if (response.success) {
          console.log("✅ Ürün başarıyla silindi:", response.deleted);

          const updatedProducts = products.filter(product => product.id !== currentProduct.id);
          setProducts(updatedProducts);

          setAllProductsData(prev => ({
            ...prev,
            [currentCategory.liability]: updatedProducts
          }));

          if (filteredProducts.length === 1) {
            setSelectedProduct(0);
          } else if (selectedProduct >= filteredProducts.length - 1) {
            setSelectedProduct(Math.max(0, filteredProducts.length - 2));
          }

          setOriginalProducts(JSON.parse(JSON.stringify(updatedProducts)));
          setHasUnsavedChanges(false);

          setShowSaveSuccess(true);
          setTimeout(() => {
            setShowSaveSuccess(false);
          }, 3000);

          console.log("🗑️ Ürün state'den kaldırıldı:", currentProduct.uniqcode);
        } else {
          throw new Error(response.message || 'Ürün silinirken hata oluştu');
        }
      } catch (error) {
        console.error('Ürün silme hatası:', error);
        setError('Ürün silinirken hata oluştu: ' + error.message);
      } finally {
        setSaving(false);
      }
    }
  };

  // Kategori ekleme fonksiyonu - GÜNCELLENMİŞ (R2 ÖNCELİKLİ)
  const handleAddCategory = async () => {
    const categoryName = newCategory.label.trim();
    
    if (!categoryName) {
      setError('Lütfen kategori adını girin');
      return;
    }

    // Aynı isimde kategori kontrolü
    const existingCategory = categories.find(
      cat => cat.label.toLowerCase() === categoryName.toLowerCase()
    );

    if (existingCategory) {
      setError(`"${categoryName}" isimli bir kategori zaten mevcut!`);
      return;
    }

    if (!newCategory.iconFile) {
      setError('Lütfen bir kategori fotoğrafı seçin');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const file = newCategory.iconFile;
      const maxSize = 8 * 1024 * 1024;

      if (file.size > maxSize) {
        setError('Dosya boyutu 8MB\'dan küçük olmalıdır');
        setUploading(false);
        return;
      }

      console.log('Kategori için dosya yükleniyor:', {
        name: file.name,
        type: file.type,
        size: file.size
      });

      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const fileName = `category_${timestamp}.${fileExtension}`;
      
      // R2'ye yükle
      const imageUrl = await apiService.uploadImageToR2(file, fileName, 'categories');

      const categoryId = categoryName;
      const card = getAuthCard();

      const newCategoryObj = {
        id: categoryId,
        label: categoryName,
        icon: imageUrl,
        liability: `liability_${Date.now()}`,
        card: card,
        isitem: "menuItems"
      };

      // Backend'e kategori ekle
      await apiService.addCategory(newCategoryObj);

      // State'i güncelle
      const updatedCategories = [...categories, newCategoryObj];
      setCategories(updatedCategories);

      // AllProductsData'yı güncelle
      setAllProductsData(prev => ({
        ...prev,
        [newCategoryObj.liability]: []
      }));

      setOriginalCategories(JSON.parse(JSON.stringify(updatedCategories)));
      setHasUnsavedChanges(false);
      setHasCategoryUnsavedChanges(false);

      setShowAddCategoryModal(false);
      setNewCategory({
        label: '',
        icon: '',
        iconFile: null
      });
      setSelectedCategory(newCategoryObj);
      setSelectedProduct(0);

      setShowSaveSuccess(true);
      setTimeout(() => {
        setShowSaveSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Kategori resim yükleme hatası:', error);
      setError('Resim yüklenemedi: ' + error.message);
      setUploading(false);
      return;
    } finally {
      setUploading(false); // Yükleme tamamlandığında veya hata oluştuğunda uploading'i false yap
    }
  };

  // Kategori silme fonksiyonu
  const removeCategory = async (categoryId) => {
    if (categories.length > 1) {
      try {
        setSaving(true);

        const categoryToRemove = categories.find(cat => cat.id === categoryId);

        if (!categoryToRemove) {
          throw new Error('Silinecek kategori bulunamadı');
        }

        console.log('🗑️ Kategori siliniyor:', categoryToRemove);

        const response = await apiService.deleteCategory(categoryToRemove.liability);

        if (response.success) {
          const updatedCategories = categories.filter(cat => cat.id !== categoryId);
          setCategories(updatedCategories);

          const updatedAllProducts = { ...allProductsData };
          delete updatedAllProducts[categoryToRemove.liability];
          setAllProductsData(updatedAllProducts);

          const updatedProducts = products.filter(product => product.liability !== categoryToRemove.liability);
          setProducts(updatedProducts);

          setOriginalCategories(JSON.parse(JSON.stringify(updatedCategories)));
          setOriginalProducts(JSON.parse(JSON.stringify(updatedProducts)));
          setHasUnsavedChanges(false);
          setHasCategoryUnsavedChanges(false);

          if (selectedCategory?.id === categoryId) {
            setSelectedCategory(updatedCategories[0] || null);
            setSelectedProduct(0);
          }

          setShowDeleteSuccess(true);
          setTimeout(() => {
            setShowDeleteSuccess(false);
          }, 3000);

          console.log('✅ Kategori başarıyla silindi');
        } else {
          throw new Error(response.message || 'Kategori silinirken hata oluştu');
        }
      } catch (error) {
        console.error('Kategori silme hatası:', error);
        setError('Kategori silinirken hata oluştu: ' + error.message);
      } finally {
        setSaving(false);
        setShowDeleteCategoryModal(false);
        setCategoryToDelete(null);
      }
    }
  };

  const handleDeleteCategory = (categoryId) => {
    setCategoryToDelete(categoryId);
    setShowDeleteCategoryModal(true);
  };

  const confirmDeleteCategory = () => {
    if (categoryToDelete) {
      removeCategory(categoryToDelete);
    }
  };

  const cancelDeleteCategory = () => {
    setShowDeleteCategoryModal(false);
    setCategoryToDelete(null);
  };

  const toggleFlag = (flagType, questionIndex, optionIndex) => {
    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

    if (productIndex === -1) return;

    if (optionIndex !== undefined) {
      updatedProducts[productIndex].questions[questionIndex].options[optionIndex][flagType] =
        !updatedProducts[productIndex].questions[questionIndex].options[optionIndex][flagType];
    } else if (questionIndex !== undefined) {
      updatedProducts[productIndex].questions[questionIndex][flagType] =
        !updatedProducts[productIndex].questions[questionIndex][flagType];
    } else {
      updatedProducts[productIndex][flagType] = !updatedProducts[productIndex][flagType];
    }

    setProducts(updatedProducts);
  };

  const startEditing = (field, value = '') => {
    setEditingField(field);
    setTempValue(value);
  };

  const handleProductSelect = (index) => {
    if (selectedProduct === index) return;

    if (hasUnsavedChanges) {
      setPendingProductIndex(index);
      setPendingAction('switch');
      setShowUnsavedChangesModal(true);
    } else {
      setSelectedProduct(index);
      setActiveQuestion(0);
    }
  };

  const handleCategorySelect = (category) => {
    if (selectedCategory?.id === category.id) return;

    if (hasUnsavedChanges) {
      setPendingAction('category_switch');
      setShowUnsavedChangesModal(true);
      setTimeout(() => {
        setSelectedCategory(category);
        setSelectedProduct(0);
      }, 100);
    } else {
      setSelectedCategory(category);
      setSelectedProduct(0);
      setActiveQuestion(0);
    }
  };

  // Design Panel butonu için click handler
  const handleDesignPanelClick = () => {
    if (hasUnsavedChanges) {
      setPendingRedirect('/designpanel');
      setShowUnsavedChangesModal(true);
    } else {
      window.location.href = '/designpanel';
    }
  };

  const executeProductSwitch = () => {
    setSelectedProduct(pendingProductIndex);
    setActiveQuestion(0);
  };

  const handleAddQuestion = () => {
    if (newQuestion.text.trim() && currentProduct) {
      const updatedProducts = [...products];
      const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

      if (productIndex !== -1) {
        if (!updatedProducts[productIndex].questions) {
          updatedProducts[productIndex].questions = [];
        }

        updatedProducts[productIndex].questions.push({
          ...newQuestion,
          hasQuantity: true,
          id: Date.now(),
          options: [
            {
              id: Date.now() + 1,
              name: "New Option",
              price: 0,
              image: "",
              hasQuantity: false
            }
          ]
        });

        setProducts(updatedProducts);
        setShowAddQuestionModal(false);
        setNewQuestion({
          text: '',
          type: 'multiple',
          isRequired: false
        });

        const newQuestionIndex = updatedProducts[productIndex].questions.length - 1;
        setActiveQuestion(newQuestionIndex);
      }
    }
  };

  const addNewOption = (questionIndex) => {
    if (!currentProduct) return;

    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

    if (productIndex !== -1) {
      if (!updatedProducts[productIndex].questions[questionIndex].options) {
        updatedProducts[productIndex].questions[questionIndex].options = [];
      }

      updatedProducts[productIndex].questions[questionIndex].options.push({
        id: Date.now(),
        name: "New Option",
        price: 0,
        image: "",
        hasQuantity: false
      });
      setProducts(updatedProducts);
    }
  };

  const deleteOption = (questionIndex, optionIndex) => {
    if (!currentProduct) return;

    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

    if (productIndex !== -1) {
      updatedProducts[productIndex].questions[questionIndex].options.splice(optionIndex, 1);
      setProducts(updatedProducts);
    }
  };

  const deleteQuestion = (questionIndex) => {
    if (!currentProduct) return;

    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

    if (productIndex !== -1) {
      updatedProducts[productIndex].questions.splice(questionIndex, 1);
      setProducts(updatedProducts);
      if (activeQuestion >= questionIndex) {
        setActiveQuestion(Math.max(0, activeQuestion - 1));
      }
    }
  };

  const validatePrice = (value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Please enter a price';
    }
    if (isNaN(value) || parseFloat(value) < 0) {
      return 'Please enter a valid price';
    }
    return '';
  };

  const updatePrice = (field, value) => {
    const error = validatePrice(value);
    if (error) {
      setPriceErrors(prev => ({ ...prev, [field]: error }));
      return;
    }

    setPriceErrors(prev => ({ ...prev, [field]: '' }));
    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

    if (productIndex !== -1) {
      updatedProducts[productIndex][field] = parseFloat(value) || 0;
      setProducts(updatedProducts);
    }
  };

  const updateOptionPrice = (questionIndex, optionIndex, value) => {
    const fieldKey = `option-${questionIndex}-${optionIndex}`;
    const error = validatePrice(value);
    if (error) {
      setPriceErrors(prev => ({ ...prev, [fieldKey]: error }));
      return;
    }

    setPriceErrors(prev => ({ ...prev, [fieldKey]: '' }));
    const updatedProducts = [...products];
    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

    if (productIndex !== -1) {
      updatedProducts[productIndex].questions[questionIndex].options[optionIndex].price = parseFloat(value) || 0;
      setProducts(updatedProducts);
    }
  };

  const handleBasePriceChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      const updatedProducts = [...products];
      const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

      if (productIndex !== -1) {
        updatedProducts[productIndex].basePrice = '';
        setProducts(updatedProducts);
        setPriceErrors(prev => ({ ...prev, basePrice: 'Please enter a price' }));
      }
    } else {
      updatePrice('basePrice', value);
    }
  };

  const handleOptionPriceChange = (questionIndex, optionIndex, e) => {
    const value = e.target.value;
    if (value === '') {
      const updatedProducts = [...products];
      const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

      if (productIndex !== -1) {
        updatedProducts[productIndex].questions[questionIndex].options[optionIndex].price = '';
        setProducts(updatedProducts);
        setPriceErrors(prev => ({ ...prev, [`option-${questionIndex}-${optionIndex}`]: 'Please enter a price' }));
      }
    } else {
      updateOptionPrice(questionIndex, optionIndex, value);
    }
  };

  const handleBasePriceBlur = (e) => {
    const value = e.target.value;
    if (value === '') {
      setPriceErrors(prev => ({ ...prev, basePrice: 'Please enter a price' }));
    } else {
      updatePrice('basePrice', value);
    }
  };

  const handleOptionPriceBlur = (questionIndex, optionIndex, e) => {
    const value = e.target.value;
    if (value === '') {
      setPriceErrors(prev => ({ ...prev, [`option-${questionIndex}-${optionIndex}`]: 'Please enter a price' }));
    } else {
      updateOptionPrice(questionIndex, optionIndex, value);
    }
  };

  const handleNavigationClick = (questionIndex) => {
    setActiveQuestion(questionIndex);
  };

  const handleAddQuestionClick = () => {
    setShowAddQuestionModal(true);
  };

  const handleAddCategoryClick = () => {
    setNewCategory({
      label: '',
      icon: '',
      iconFile: null
    });
    setShowAddCategoryModal(true);
  };

  // Save and Continue fonksiyonu
  const handleSaveAndContinue = async () => {
    try {
      // Önce değişiklikleri kaydet
      if (hasUnsavedChanges) {
        const success = await saveCurrentProducts();
        if (!success) {
          // Kaydetme başarısız oldu, modal'ı kapatma
          return;
        }
      }

      // Kaydetme işlemi başarılı olduktan sonra devam et
      if (pendingAction === 'switch') {
        executeProductSwitch();
      } else if (pendingAction === 'new') {
        executeAddNewProduct();
      } else if (pendingAction === 'category_switch') {
        // kategori switch zaten handler içinde setSelectedCategory ile yönetiliyor
      }

      // Pending redirect varsa yönlendir
      if (pendingRedirect) {
        // 1 saniye gecikme (1000ms = 1 saniye)
        setTimeout(() => {
          window.location.href = pendingRedirect;
        }, 20);
        return;
      }

      setShowUnsavedChangesModal(false);
      setPendingProductIndex(null);
      setPendingAction(null);
      setPendingRedirect(null);

    } catch (error) {
      console.error('Kaydetme hatası:', error);
      setError('Değişiklikler kaydedilirken hata oluştu: ' + error.message);
    }
  };

  const handleContinueWithoutSaving = () => {
    setShowUnsavedChangesModal(false);
    const action = pendingAction;
    const index = pendingProductIndex;
    const redirectUrl = pendingRedirect;

    setPendingProductIndex(null);
    setPendingAction(null);
    setPendingRedirect(null);

    // Pending redirect varsa yönlendir
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }

    const restoredProducts = JSON.parse(JSON.stringify(originalProducts));
    const restoredCategories = JSON.parse(JSON.stringify(originalCategories));

    setProducts(restoredProducts);
    setCategories(restoredCategories);
    setHasUnsavedChanges(false);
    setHasCategoryUnsavedChanges(false);

    setTimeout(() => {
      if (action === 'switch') {
        setSelectedProduct(index);
        setActiveQuestion(0);
      } else if (action === 'new') {
        executeAddNewProduct();
      } else if (action === 'category_switch') {
        setActiveQuestion(0);
      }
    }, 100);
  };

  const handleDiscardChanges = () => {
    const restoredProducts = JSON.parse(JSON.stringify(originalProducts));
    const restoredCategories = JSON.parse(JSON.stringify(originalCategories));
    setProducts(restoredProducts);
    setCategories(restoredCategories);
    setHasUnsavedChanges(false);
    setHasCategoryUnsavedChanges(false);
    if (selectedProduct >= filteredProducts.length) {
      setSelectedProduct(Math.max(0, filteredProducts.length - 1));
    }
  };

  const handleCancelSwitch = () => {
    setShowUnsavedChangesModal(false);
    setPendingProductIndex(null);
    setPendingAction(null);
    setPendingRedirect(null);
  };

  // Loading state - Sadece ilk açılışta
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Sayfa yükleniyor...</p>
          <p className="text-sm text-gray-500 mt-2">Tüm kategoriler ve ürünler hazırlanıyor</p>
          <p className="text-xs text-gray-400 mt-1">Resimler ön yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && categories.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Hata Oluştu</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadInitialData}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors"
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* ✅ Sidebar - FIXED SCROLL ARCHITECTURE */}
      <div className="w-full md:w-80 bg-white shadow-lg md:h-screen md:sticky md:top-0 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex-shrink-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-bold text-gray-800">Merchant Panel</h2>
            <button
              onClick={handleDesignPanelClick}
              className="bg-purple-500 hover:bg-purple-600 text-white py-1 px-3 rounded-lg transition-colors text-xs font-medium"
            >
              Design Panel
            </button>
          </div>

          {hasUnsavedChanges && (
            <div className="mt-2 bg-yellow-100 border border-yellow-400 text-yellow-700 px-2 py-1 rounded text-xs">
              ⚠️ Unsaved product changes
            </div>
          )}
          {error && (
            <div className="mt-2 bg-red-100 border border-red-400 text-red-700 px-2 py-1 rounded text-xs">
              ❌ {error}
            </div>
          )}
          {showDeleteSuccess && (
            <div className="mt-2 bg-green-100 border border-green-400 text-green-700 px-2 py-1 rounded text-xs">
              ✅ Kategori başarıyla silindi
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-4 flex-1 min-h-0 overflow-hidden flex flex-col">
          {/* Butonlar - sabit yükseklik */}
          <div className="flex space-x-2 mb-4 flex-shrink-0">
            <button
              onClick={handleAddCategoryClick}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-lg transition-colors font-medium text-sm"
            >
              + Add Category
            </button>
            <button
              onClick={addNewProduct}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-lg transition-colors font-medium text-sm"
            >
              + Add Product
            </button>
          </div>

          {/* ✅ Split area (no parent scrollbar) */}
          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
            {/* Kategoriler */}
            <div className="flex flex-col min-h-0 max-h-[40%] md:max-h-[45%] overflow-hidden">
              <h3 className="font-semibold text-gray-700 mb-3 flex-shrink-0">Categories</h3>
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                <div className="space-y-2 pr-1">
                  {categories.length > 0 ? (
                    categories.map((category) => {
                      const count = getCategoryProductCount(category);
                      return (
                        <div
                          key={category.id}
                          className={`p-3 rounded-lg cursor-pointer transition-all border ${
                            selectedCategory?.id === category.id
                              ? 'bg-blue-50 border-blue-300 shadow-sm'
                              : 'bg-white border-gray-200 hover:border-blue-200 hover:shadow-sm'
                          }`}
                          onClick={() => handleCategorySelect(category)}
                        >
                          {selectedCategory?.id === category.id ? (
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <CategoryImage
                                  src={category.icon}
                                  alt={category.label}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-medium text-gray-800 text-sm">
                                      {category.label}
                                    </h4>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-xs text-gray-500">
                                    {count} product{count !== 1 ? 's' : ''}
                                  </span>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCategory(category);
                                      }}
                                      className="text-blue-500 hover:text-blue-700 text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded"
                                    >
                                      ✏️ Edit
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(category.id);
                                      }}
                                      className="text-red-500 hover:text-red-700 text-xs bg-red-50 hover:bg-red-100 px-2 py-1 rounded"
                                      disabled={categories.length <= 1}
                                    >
                                      🗑️ Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-800 text-sm">
                                  {category.label}
                                </h4>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="text-xs text-gray-500">
                                    {count} product{count !== 1 ? 's' : ''}
                                  </span>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditCategory(category);
                                      }}
                                      className="text-blue-500 hover:text-blue-700 text-xs"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(category.id);
                                      }}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                      disabled={categories.length <= 1}
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center p-4 text-gray-500 text-sm">
                      No categories found
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Ürünler */}
            <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
              <h3 className="font-semibold text-gray-700 mb-3 flex-shrink-0">
                Products in {currentCategory?.label}
              </h3>
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                <div className="space-y-3 pr-1 pb-10">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product, index) => (
                      <div
                        key={product.id}
                        className={`p-3 rounded-lg cursor-pointer transition-all border ${
                          selectedProduct === index
                            ? 'bg-blue-50 border-blue-300 shadow-sm'
                            : 'bg-white border-gray-200 hover:border-blue-200 hover:shadow-sm'
                        }`}
                        onClick={() => handleProductSelect(index)}
                      >
                        <div className="flex items-center space-x-3">
                          <ProductListImage
                            src={product.image}
                            alt={product.name}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-800 truncate text-sm">
                                  {product.name}
                                </h4>
                                <p className="text-green-600 font-semibold text-sm">
                                  ${product.basePrice || 0}
                                </p>
                              </div>
                              <div className={`w-2 h-2 rounded-full ${
                                product.active !== false ? 'bg-green-500' : 'bg-red-500'
                              }`}></div>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-xs text-gray-500">
                                {product.questions ? product.questions.length : 0} question
                                {product.questions && product.questions.length !== 1 ? 's' : ''}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                product.active !== false
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {product.active !== false ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-4 text-gray-500 text-sm">
                      No products in this category
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-6 overflow-auto">
        {currentProduct ? (
          <div className="max-w-6xl mx-auto">
            {/* Product Header */}
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between mb-6 space-y-4 lg:space-y-0">
                <div className="flex flex-col md:flex-row md:items-start space-y-4 md:space-y-0 md:space-x-6 flex-1">
                  {/* Product Image */}
                  <div className="relative self-start">
                    <ProductImage
                      src={currentProduct.image}
                      alt={currentProduct.name}
                      onImageChange={handleProductImageUpload}
                      uploading={uploading}
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 space-y-2 md:space-y-0">
                      {editingField === 'name' ? (
                        <input
                          type="text"
                          value={tempValue}
                          onChange={(e) => setTempValue(e.target.value)}
                          onBlur={() => updateProductField('name', tempValue)}
                          onKeyPress={(e) => e.key === 'Enter' && updateProductField('name', tempValue)}
                          className="text-xl md:text-2xl font-bold border-b-2 border-blue-500 outline-none w-full md:w-auto"
                          autoFocus
                        />
                      ) : (
                        <h1
                          className="text-xl md:text-2xl font-bold text-gray-800 cursor-pointer hover:bg-gray-100 p-1 rounded"
                          onClick={() => startEditing('name', currentProduct.name)}
                        >
                          {currentProduct.name}
                        </h1>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Base Price</label>
                        <div className="flex items-center">
                          <div className="relative">
                            <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg px-3 py-2 w-32">
                              <span className="text-gray-500">$</span>
                              <input
                                type="text"
                                value={currentProduct.basePrice}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    const updatedProducts = [...products];
                                    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

                                    if (productIndex !== -1) {
                                      updatedProducts[productIndex].basePrice = '';
                                      setProducts(updatedProducts);
                                      setPriceErrors(prev => ({ ...prev, basePrice: 'Please enter a price' }));
                                    }
                                  } else {
                                    const error = (() => {
                                      if (value === '' || value === null || value === undefined) return 'Please enter a price';
                                      if (isNaN(value) || parseFloat(value) < 0) return 'Please enter a valid price';
                                      return '';
                                    })();
                                    if (error) {
                                      setPriceErrors(prev => ({ ...prev, basePrice: error }));
                                      return;
                                    }
                                    setPriceErrors(prev => ({ ...prev, basePrice: '' }));

                                    const updatedProducts = [...products];
                                    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

                                    if (productIndex !== -1) {
                                      updatedProducts[productIndex].basePrice = parseFloat(value) || 0;
                                      setProducts(updatedProducts);
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value;
                                  if (value === '') {
                                    setPriceErrors(prev => ({ ...prev, basePrice: 'Please enter a price' }));
                                  } else {
                                    const error = (() => {
                                      if (value === '' || value === null || value === undefined) return 'Please enter a price';
                                      if (isNaN(value) || parseFloat(value) < 0) return 'Please enter a valid price';
                                      return '';
                                    })();
                                    if (error) {
                                      setPriceErrors(prev => ({ ...prev, basePrice: error }));
                                      return;
                                    }
                                    setPriceErrors(prev => ({ ...prev, basePrice: '' }));

                                    const updatedProducts = [...products];
                                    const productIndex = updatedProducts.findIndex(p => p.id === currentProduct.id);

                                    if (productIndex !== -1) {
                                      updatedProducts[productIndex].basePrice = parseFloat(value) || 0;
                                      setProducts(updatedProducts);
                                    }
                                  }
                                }}
                                className="text-lg md:text-xl font-semibold text-green-600 outline-none w-full bg-transparent"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                        {priceErrors.basePrice && (
                          <p className="text-red-500 text-xs mt-1">{priceErrors.basePrice}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Product Description</label>
                        {editingField === 'description' ? (
                          <textarea
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            onBlur={() => updateProductField('description', tempValue)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-16"
                            placeholder="Enter product description..."
                            autoFocus
                          />
                        ) : (
                          <p
                            className="text-gray-600 cursor-pointer hover:bg-gray-100 p-2 rounded border border-transparent hover:border-gray-300 text-sm min-h-16 flex items-start"
                            onClick={() => startEditing('description', currentProduct.description || '')}
                          >
                            {currentProduct.description || "Click to add product description..."}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-72 lg:ml-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3 text-center">Product Preview</h3>
                    <ProductPreviewCard product={currentProduct} />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center justify-between space-y-3 md:space-y-0 pt-4 border-t border-gray-200">
                <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-3">
                  <button
                    onClick={handleSaveChanges}
                    className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-300 ${
                      hasUnsavedChanges
                        ? 'bg-green-500 hover:bg-green-600 text-white transform hover:scale-105 shadow-lg'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                    }`}
                    disabled={!hasUnsavedChanges || saving}
                  >
                    <span className="text-lg">💾</span>
                    <span className="font-semibold">
                      {saving ? 'Saving...' : 'Save Changes'}
                    </span>
                    {hasUnsavedChanges && !saving && (
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    )}
                  </button>

                  {hasUnsavedChanges && (
                    <button
                      onClick={handleDiscardChanges}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-all duration-300"
                      disabled={saving}
                    >
                      <span className="text-lg">↶</span>
                      <span className="font-semibold">Discard Changes</span>
                    </button>
                  )}

                  <button
                    onClick={removeProduct}
                    className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-all duration-300"
                    disabled={saving}
                  >
                    <span className="text-lg">🗑️</span>
                    <span className="font-semibold">Remove This Product</span>
                  </button>
                </div>

                {showSaveSuccess && (
                  <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg animate-fade-in">
                    <div className="flex items-center justify-center space-x-2">
                      <span>✓</span>
                      <span className="text-sm">Changes saved successfully!</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {currentProduct.questions && currentProduct.questions.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Questions Navigation</h3>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {currentProduct.questions.map((question, index) => (
                    <button
                      key={question.id}
                      onClick={() => handleNavigationClick(index)}
                      className={`px-3 py-2 rounded-lg transition-colors text-sm ${
                        activeQuestion === index
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {question.text.length > 25
                        ? question.text.substring(0, 25) + '...'
                        : question.text
                      }
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={questionsSectionRef} className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between space-y-2 md:space-y-0">
                <h2 className="text-xl font-bold text-gray-800">Customization Questions</h2>
                <button
                  onClick={handleAddQuestionClick}
                  className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors text-sm md:text-base w-full md:w-auto"
                >
                  + Add Question
                </button>
              </div>

              {currentProduct.questions && currentProduct.questions.length > 0 ? (
                currentProduct.questions.map((question, questionIndex) => (
                  <div
                    key={question.id}
                    ref={el => questionRefs.current[questionIndex] = el}
                    className={`bg-white rounded-lg shadow-md p-4 md:p-6 transition-all duration-300 ${
                      activeQuestion === questionIndex ? 'border-2 border-blue-500' : 'border border-gray-200'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 space-y-2 md:space-y-0">
                      <div className="flex-1">
                        {editingField === `question-${questionIndex}-text` ? (
                          <input
                            type="text"
                            value={tempValue}
                            onChange={(e) => setTempValue(e.target.value)}
                            onBlur={() => updateQuestionField(questionIndex, 'text', tempValue)}
                            onKeyPress={(e) => e.key === 'Enter' && updateQuestionField(questionIndex, 'text', tempValue)}
                            className="text-lg font-semibold border-b-2 border-blue-500 outline-none w-full"
                            autoFocus
                          />
                        ) : (
                          <h3
                            className="text-lg font-semibold text-gray-800 cursor-pointer hover:bg-gray-100 p-1 rounded"
                            onClick={() => startEditing(`question-${questionIndex}-text`, question.text)}
                          >
                            {question.text}
                          </h3>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 md:flex-nowrap md:items-center md:space-x-2 md:ml-4">
                        <select
                          value={question.isRequired ? 'required' : 'optional'}
                          onChange={(e) => updateQuestionField(questionIndex, 'isRequired', e.target.value === 'required')}
                          className="border rounded px-2 py-1 text-sm w-full md:w-auto"
                        >
                          <option value="optional">Optional</option>
                          <option value="required">Required</option>
                        </select>

                        <select
                          value={question.type}
                          onChange={(e) => updateQuestionField(questionIndex, 'type', e.target.value)}
                          className="border rounded px-2 py-1 text-sm w-full md:w-auto"
                        >
                          <option value="multiple">Multiple Choice</option>
                          <option value="single">Single Choice</option>
                        </select>

                        <button
                          onClick={() => deleteQuestion(questionIndex)}
                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded text-sm w-full md:w-auto"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between space-y-2 md:space-y-0">
                        <h4 className="font-medium text-gray-700">Options</h4>
                        <button
                          onClick={() => addNewOption(questionIndex)}
                          className="bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded text-sm w-full md:w-auto"
                        >
                          + Add Option
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {question.options && question.options.map((option, optionIndex) => (
                          <div key={option.id} className="border rounded-lg p-4">
                            <div className="flex space-x-4">
                              {/* Option Image */}
                              <div className="relative">
                                <OptionImage
                                  src={option.image}
                                  alt={option.name}
                                  onImageChange={(e) => handleOptionImageUpload(e, questionIndex, optionIndex)}
                                  uploading={uploading}
                                />
                              </div>

                              <div className="flex-1">
                                <div className="mb-2">
                                  {editingField === `option-${questionIndex}-${optionIndex}-name` ? (
                                    <input
                                      type="text"
                                      value={tempValue}
                                      onChange={(e) => setTempValue(e.target.value)}
                                      onBlur={() => updateOptionField(questionIndex, optionIndex, 'name', tempValue)}
                                      onKeyPress={(e) => e.key === 'Enter' && updateOptionField(questionIndex, optionIndex, 'name', tempValue)}
                                      className="font-medium border-b-2 border-blue-500 outline-none w-full"
                                      autoFocus
                                    />
                                  ) : (
                                    <p
                                      className="font-medium text-gray-800 cursor-pointer hover:bg-gray-100 p-1 rounded"
                                      onClick={() => startEditing(`option-${questionIndex}-${optionIndex}-name`, option.name)}
                                    >
                                      {option.name}
                                    </p>
                                  )}
                                </div>

                                <div className="space-y-2">
                                  <div>
                                    <label className="text-xs text-gray-500 mb-1 block">Price</label>
                                    <div className="flex items-center space-x-2">
                                      <div className="flex items-center space-x-1 bg-white border border-gray-300 rounded px-2 py-1 w-24">
                                        <span className="text-gray-500 text-sm">$</span>
                                        <input
                                          type="text"
                                          value={option.price}
                                          onChange={(e) => handleOptionPriceChange(questionIndex, optionIndex, e)}
                                          onBlur={(e) => handleOptionPriceBlur(questionIndex, optionIndex, e)}
                                          className="text-green-600 font-semibold outline-none w-full bg-transparent"
                                          placeholder="0.00"
                                        />
                                      </div>
                                    </div>
                                    {priceErrors[`option-${questionIndex}-${optionIndex}`] && (
                                      <p className="text-red-500 text-xs mt-1">{priceErrors[`option-${questionIndex}-${optionIndex}`]}</p>
                                    )}
                                  </div>

                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => toggleFlag('hasQuantity', questionIndex, optionIndex)}
                                      className={`px-2 py-1 rounded text-xs ${
                                        option.hasQuantity ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                      }`}
                                    >
                                      {option.hasQuantity ? 'Has Quantity' : 'No Quantity'}
                                    </button>
                                  </div>

                                  <button
                                    onClick={() => deleteOption(questionIndex, optionIndex)}
                                    className="bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded text-xs"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <p className="text-gray-500 text-lg">No questions yet. Add your first question to get started!</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500 text-lg">
                {filteredProducts.length === 0
                  ? `No products available in ${currentCategory?.label} category`
                  : "No product selected"}
              </p>
              <button
                onClick={addNewProduct}
                className="mt-4 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors"
              >
                + Add New Product
              </button>
            </div>
          </div>
        )}

        {/* Add Question Modal */}
        {showAddQuestionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Question</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Text
                  </label>
                  <input
                    type="text"
                    value={newQuestion.text}
                    onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                    placeholder="Enter question text..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Question Type
                    </label>
                    <select
                      value={newQuestion.type}
                      onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="multiple">Multiple Choice</option>
                      <option value="single">Single Choice</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Required
                    </label>
                    <select
                      value={newQuestion.isRequired ? 'required' : 'optional'}
                      onChange={(e) => setNewQuestion({ ...newQuestion, isRequired: e.target.value === 'required' })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="optional">Optional</option>
                      <option value="required">Required</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowAddQuestionModal(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddQuestion}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Add Question
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Category Modal */}
        {showAddCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Category</h3>

              {error && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={newCategory.label}
                    onChange={(e) => setNewCategory({ ...newCategory, label: e.target.value })}
                    placeholder="Enter category name..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Icon *
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                        {newCategory.iconFile ? (
                          <img
                            src={URL.createObjectURL(newCategory.iconFile)}
                            alt="Category icon preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center">
                            <span>No Icon</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="block bg-blue-500 hover:bg-blue-600 text-white text-center py-2 px-3 rounded-lg transition-colors cursor-pointer">
                        {newCategory.iconFile ? 'Change Icon' : 'Upload Icon'}
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setNewCategory({
                                ...newCategory,
                                iconFile: file,
                                icon: URL.createObjectURL(file)
                              });
                              setError(null);
                            }
                          }}
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {newCategory.iconFile ? `Selected: ${newCategory.iconFile.name}` : 'No file selected'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Supported formats: JPG, JPEG, PNG, WebP, GIF, SVG, BMP, TIFF (Max. 8MB)
                      </p>
                    </div>
                  </div>
                </div>

                {uploading && (
                  <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                      <span>Fotoğraf yükleniyor...</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowAddCategoryModal(false);
                      setError(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={uploading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddCategory}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      uploading || !newCategory.label.trim() || !newCategory.iconFile
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                    disabled={uploading || !newCategory.label.trim() || !newCategory.iconFile}
                  >
                    {uploading ? 'Uploading...' : 'Add Category'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Category Modal */}
        {showEditCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Category</h3>

              {error && (
                <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={editingCategoryData.label}
                    onChange={(e) => setEditingCategoryData({ ...editingCategoryData, label: e.target.value })}
                    placeholder="Enter category name..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={saving}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Category ID otomatik olarak aynı değere ayarlanacak: <strong>{editingCategoryData.label}</strong>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Eski kategori adı: <strong>{editingCategoryData.oldLabel}</strong>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Icon
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                        {editingCategoryData.icon ? (
                          <img
                            src={editingCategoryData.icon}
                            alt="Category icon"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="text-gray-500 text-xs text-center w-full h-full flex flex-col items-center justify-center">
                            <span>No Icon</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className={`block text-center py-2 px-3 rounded-lg transition-colors cursor-pointer ${
                        saving ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 text-white'
                      }`}>
                        Upload Icon
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (evt) => {
                                setEditingCategoryData({ ...editingCategoryData, icon: evt.target.result });
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          disabled={saving}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {saving && (
                  <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                      <span>Kategori güncelleniyor...</span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={handleEditCategoryCancel}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditCategorySave}
                    disabled={saving || !editingCategoryData.label.trim()}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      saving || !editingCategoryData.label.trim()
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unsaved Changes Modal */}
        {showUnsavedChangesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {pendingRedirect ? "Design Panel'a Geçiş" : 'Unsaved Changes'}
              </h3>

              <div className="space-y-4">
                <p className="text-gray-600">
                  {pendingRedirect 
                    ? "Kaydedilmemiş değişiklikleriniz var. Design Panel'a geçmeden önce ne yapmak istiyorsunuz?"
                    : 'You have unsaved changes. What would you like to do?'}
                </p>

                <div className="flex flex-col space-y-3 pt-4">
                  <button
                    onClick={handleSaveAndContinue}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg transition-colors font-medium"
                    disabled={saving}
                  >
                    {saving 
                      ? 'Kaydediliyor...' 
                      : pendingRedirect
                        ? "Değişiklikleri Kaydet ve Design Panel'a Geç"
                        : 'Save Changes and Continue'}
                  </button>

                  <button
                    onClick={handleContinueWithoutSaving}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg transition-colors font-medium"
                    disabled={saving}
                  >
                    {pendingRedirect
                      ? "Kaydetmeden Design Panel'a Geç"
                      : 'Continue Without Saving'}
                  </button>

                  <button
                    onClick={handleCancelSwitch}
                    className="w-full bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg transition-colors font-medium"
                    disabled={saving}
                  >
                    İptal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Category Confirmation Modal */}
        {showDeleteCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Delete Category</h3>

              <div className="space-y-4">
                <p className="text-gray-600">
                  Bu kategoriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve bu kategoriye ait tüm ürünler de silinecektir.
                </p>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={cancelDeleteCategory}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={saving}
                  >
                    İptal
                  </button>
                  <button
                    onClick={confirmDeleteCategory}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    disabled={saving}
                  >
                    {saving ? 'Deleting...' : 'Evet, Sil'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MerchantProductManagement;